import { z } from 'zod';

// =========================================
// User Groups (from requirements 0.2, 0.3)
// =========================================

export const InternalUserGroup = {
  ADMIN: 'admin',
  FINANCE: 'finance',
  BUYER: 'buyer',
  SUPERVISOR: 'supervisor',
  SITE_MANAGER: 'site-manager',
  STOREKEEPER: 'storekeeper',
  RECEIVING: 'receiving',
  CHEF: 'chef',
  VENUE_STAFF: 'venue-staff',
} as const;

export type InternalUserGroup = (typeof InternalUserGroup)[keyof typeof InternalUserGroup];

export const SupplierUserGroup = {
  SUPPLIER_ADMIN: 'supplier-admin',
  SUPPLIER_SALES: 'supplier-sales',
  SUPPLIER_DELIVERY: 'supplier-delivery',
} as const;

export type SupplierUserGroup = (typeof SupplierUserGroup)[keyof typeof SupplierUserGroup];

// =========================================
// Permissions (granular access control)
// =========================================

export const Permission = {
  // User management
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  
  // Catalog
  MANAGE_CATALOG: 'manage_catalog',
  VIEW_CATALOG: 'view_catalog',
  
  // Recipes
  MANAGE_RECIPES: 'manage_recipes',
  VIEW_RECIPES: 'view_recipes',
  
  // Menus
  MANAGE_MENUS: 'manage_menus',
  VIEW_MENUS: 'view_menus',
  
  // Inventory
  MANAGE_INVENTORY: 'manage_inventory',
  VIEW_INVENTORY: 'view_inventory',
  TRANSFER_INVENTORY: 'transfer_inventory',
  
  // Requisitions
  APPROVE_REQUISITIONS: 'approve_requisitions',
  CREATE_REQUISITIONS: 'create_requisitions',
  VIEW_REQUISITIONS: 'view_requisitions',
  
  // Receiving
  RECEIVE_GOODS: 'receive_goods',
  VIEW_RECEIVING: 'view_receiving',
  
  // Procurement
  MANAGE_SOURCING: 'manage_sourcing',
  APPROVE_POS: 'approve_pos',
  VIEW_POS: 'view_pos',
  
  // Suppliers
  MANAGE_SUPPLIERS: 'manage_suppliers',
  VIEW_SUPPLIERS: 'view_suppliers',
  
  // Finance
  MANAGE_INVOICES: 'manage_invoices',
  VIEW_INVOICES: 'view_invoices',
  VIEW_SPEND: 'view_spend',
  
  // Analytics
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_REPORTS: 'export_reports',
  
  // Waste
  LOG_WASTE: 'log_waste',
  VIEW_WASTE: 'view_waste',
  
  // Production
  MANAGE_PRODUCTION: 'manage_production',
  VIEW_PRODUCTION: 'view_production',
  
  // Settings
  MANAGE_SETTINGS: 'manage_settings',
  VIEW_SETTINGS: 'view_settings',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

// =========================================
// User Profile Schema
// =========================================

export const UserProfileSchema = z.object({
  // Identity
  userId: z.string(),
  email: z.string().email(),
  username: z.string().optional(),
  
  // Name
  givenName: z.string(),
  familyName: z.string(),
  
  // Contact
  phoneNumber: z.string().optional(),
  
  // Organization
  organizationId: z.string(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  
  // Preferences
  preferredLanguage: z.string().optional(),
  locale: z.string().optional(),
  
  // Access control
  groups: z.array(z.string()),
  siteAccess: z.array(z.string()),
  venueAccess: z.array(z.string()),
  defaultSiteId: z.string().optional(),
  
  // Supplier-specific (only for supplier portal users)
  supplierId: z.string().optional(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

// =========================================
// User Session (runtime auth state)
// =========================================

export const UserSessionSchema = z.object({
  userId: z.string(),
  email: z.string().email(),
  organizationId: z.string(),
  groups: z.array(z.string()),
  siteAccess: z.array(z.string()),
  venueAccess: z.array(z.string()),
  
  // Profile info for display
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().optional(),
  preferredLanguage: z.string().optional(),
  defaultSiteId: z.string().optional(),
});

export type UserSession = z.infer<typeof UserSessionSchema>;

// =========================================
// Portal Type
// =========================================

export type PortalType = 'internal' | 'supplier';

// =========================================
// Access Control Scope
// =========================================

// ORGANIZATION: Full org access (admin, finance, buyer)
// SITE: Site-level access (supervisor, site-manager, storekeeper, receiving)
// VENUE: Venue-level access (chef, venue-staff)
export const AccessScope = {
  ORGANIZATION: 'organization',
  SITE: 'site',
  VENUE: 'venue',
} as const;

export type AccessScope = (typeof AccessScope)[keyof typeof AccessScope];

// Group to scope mapping (from requirements 0.2)
export const GROUP_SCOPE_MAP: Record<InternalUserGroup, AccessScope> = {
  [InternalUserGroup.ADMIN]: AccessScope.ORGANIZATION,
  [InternalUserGroup.FINANCE]: AccessScope.ORGANIZATION,
  [InternalUserGroup.BUYER]: AccessScope.ORGANIZATION,
  [InternalUserGroup.SUPERVISOR]: AccessScope.SITE,
  [InternalUserGroup.SITE_MANAGER]: AccessScope.SITE,
  [InternalUserGroup.STOREKEEPER]: AccessScope.SITE,
  [InternalUserGroup.RECEIVING]: AccessScope.SITE,
  [InternalUserGroup.CHEF]: AccessScope.VENUE,
  [InternalUserGroup.VENUE_STAFF]: AccessScope.VENUE,
};
